import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'

const REFETCH_INTERVALS = [15_000, 30_000, 60_000, 100_000, 130_000]
const MAX_REFETCH_COUNT = 5

export const useRetryMissionList = () => {
  const queryClient = useQueryClient()
  const timeoutRefs = useRef<NodeJS.Timeout[]>([])
  const refetchCountRef = useRef(0)
  const isRetryingRef = useRef(false)

  const stopRetrying = useCallback(() => {
    timeoutRefs.current.forEach((timeout) => clearTimeout(timeout))
    timeoutRefs.current = []
    isRetryingRef.current = false
    refetchCountRef.current = 0
  }, [])

  const startRetrying = useCallback(() => {
    if (isRetryingRef.current) {
      return
    }

    isRetryingRef.current = true
    refetchCountRef.current = 0

    const scheduleRefetch = (index: number) => {
      if (index >= REFETCH_INTERVALS.length || refetchCountRef.current >= MAX_REFETCH_COUNT) {
        stopRetrying()
        return
      }

      const timeout = setTimeout(async () => {
        // Refetch the mission list
        await queryClient.refetchQueries({ queryKey: ['MISSION_LIST'] })

        refetchCountRef.current += 1

        // Schedule next refetch if we haven't reached max count
        if (refetchCountRef.current < MAX_REFETCH_COUNT && index + 1 < REFETCH_INTERVALS.length) {
          scheduleRefetch(index + 1)
        } else {
          stopRetrying()
        }
      }, REFETCH_INTERVALS[index])

      timeoutRefs.current.push(timeout)
    }

    // Start the first refetch
    scheduleRefetch(0)
  }, [queryClient, stopRetrying])

  return { startRetrying, stopRetrying }
}
